// SYSTEM_ROUTE: /study
import { useState } from 'react';
import type { MasteryState, StudySession, StudySubject, StudySummary } from '@life-os/app-core';
import { MASTERY_STATES, masteryFraction } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function masteryTone(state: MasteryState): ChipTone {
  const f = masteryFraction(state);
  if (f >= 0.83) return 'primary';
  if (f >= 0.5) return 'neutral';
  return 'muted';
}

function ProgressBar({ fraction }: { fraction: number }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: 'var(--lc-surface-container)', overflow: 'hidden', flex: 1, minWidth: 100 }}>
      <div style={{ width: `${Math.round(fraction * 100)}%`, height: '100%', background: 'var(--lc-primary)' }} />
    </div>
  );
}

function weekRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 6);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

interface Data {
  subjects: readonly StudySubject[];
  sessions: readonly StudySession[];
}

export function StudyScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [logMinutes, setLogMinutes] = useState<Record<string, number>>({});

  const data = useAsync<Data>(async () => {
    const [subjects, sessions] = await Promise.all([services.study.listSubjects(), services.study.listSessions()]);
    return { subjects, sessions };
  }, [revision]);

  const { from, to } = weekRange();
  const summary = useAsync<StudySummary>(() => services.study.summary(from, to), [revision]);

  const subjects = data.value?.subjects ?? [];
  const s = summary.value;

  const addSubject = async () => {
    if (title.trim().length === 0) return;
    await services.study.createSubject({ title });
    setTitle('');
    refresh();
  };

  const logSession = async (subjectId: string) => {
    const minutes = logMinutes[subjectId] ?? 25;
    await services.study.logSession({ subjectId: subjectId as StudySubject['id'], minutes });
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Study & Learning</>} subtitle={<>Track what you're actively learning, move it along the mastery path, and log practice. Mastery
            is your personal state — not a grade.</>} />

      {s && s.totalMinutes > 0 ? (
        <Pane title="This week" hint={`${s.daysStudied} day${s.daysStudied === 1 ? '' : 's'} studied`}>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-md)', flexWrap: 'wrap' }}>
            <Chip tone="primary">{Math.round(s.totalMinutes / 60 * 10) / 10} h practiced</Chip>
            <Chip tone="neutral">{s.sessionCount} session{s.sessionCount === 1 ? '' : 's'}</Chip>
          </div>
        </Pane>
      ) : null}

      <Pane title="New subject">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: 1 }}
            value={title}
            placeholder="e.g. Networking, Spanish, Linear Algebra…"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addSubject();
            }}
            aria-label="Subject title"
          />
          <Button variant="primary" onClick={() => void addSubject()}>
            Add subject
          </Button>
        </div>
      </Pane>

      <Pane title="Subjects" hint={`${subjects.length}`}>
        {subjects.length === 0 ? (
          <EmptyState title="No subjects yet">
            Add something you're learning. Set its mastery honestly and log short practice sessions as you go.
          </EmptyState>
        ) : (
          <Stack>
            {subjects.map((subj) => {
              const minutes = s?.minutesBySubject[String(subj.id)] ?? 0;
              return (
                <div key={String(subj.id)} className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                    <strong style={{ flex: 1 }}>{subj.title}</strong>
                    <Chip tone={masteryTone(subj.masteryState)}>{subj.masteryState}</Chip>
                    <select
                      value={subj.masteryState}
                      onChange={(e) => void services.study.setMastery(subj.id, e.target.value as MasteryState).then(refresh)}
                      aria-label="Mastery state"
                      style={inputStyle}
                    >
                      {MASTERY_STATES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ProgressBar fraction={masteryFraction(subj.masteryState)} />
                    {minutes > 0 ? (
                      <span style={{ fontSize: 'var(--lc-text-sm)', color: 'var(--lc-on-surface-variant)' }}>{minutes}m this week</span>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)' }}>
                    <input
                      type="number"
                      min={5}
                      step={5}
                      value={logMinutes[String(subj.id)] ?? 25}
                      onChange={(e) => setLogMinutes((m) => ({ ...m, [String(subj.id)]: Math.max(5, Number(e.target.value) || 25) }))}
                      aria-label="Session minutes"
                      style={{ ...inputStyle, width: 80 }}
                    />
                    <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>min</span>
                    <Button variant="primary" onClick={() => void logSession(String(subj.id))}>
                      Log session
                    </Button>
                  </div>
                </div>
              );
            })}
          </Stack>
        )}
      </Pane>
    </>
  );
}
